import i18n from "@/shared/lib/i18n/index.ts";

export enum AlertStatus {
    Pending = "Pending",
    Resolved = "Resolved",
    Ignored = "Ignored",
}

/**
 * Alert types offered in the filter controls.
 *
 * Surveyed against the live API (14 days, ~3.5k alerts): "Boulder On Truck"
 * ~86%, "Boulder In First Dump" ~9%, "Boulder Rehandling" ~3%, bare "Boulder"
 * ~2%. The last two entries were MISSING here, so the third-largest type could
 * not be filtered for at all.
 *
 * "Dust Event" was not seen in that survey, but it covers one location only —
 * absence there is not evidence it is unused elsewhere, so it stays.
 *
 * This list is for the FILTER UI, not a closed set: the backend may emit types
 * that are not here, and every display path renders an unknown type verbatim
 * rather than hiding it.
 */
export const ALERT_TYPES = Object.freeze([
    "Boulder On Truck",
    "Boulder In First Dump",
    "Boulder Rehandling",
    "Boulder",
    "Dust Event",
] as const);

/**
 * Human-facing label for an alert `type`.
 *
 * "Boulder On Truck" shows as just "Boulder" — we can't yet confirm whether the
 * boulder is actually on a truck. That leaves the BARE "Boulder" type needing a
 * different label, or the two become indistinguishable on screen while being
 * different values on the wire; that collision is why a whole type went
 * unnoticed until the data was surveyed directly.
 *
 * The raw value is unchanged either way — still sent to the backend for
 * filtering and matching. Only the DISPLAY differs.
 */
const ALERT_TYPE_KEYS: Record<string, "alertTypes.boulderOnTruck" | "alertTypes.boulder" | "alertTypes.boulderInFirstDump" | "alertTypes.boulderRehandling" | "alertTypes.dustEvent"> = {
    "Boulder On Truck": "alertTypes.boulderOnTruck",
    "Boulder": "alertTypes.boulder",
    "Boulder In First Dump": "alertTypes.boulderInFirstDump",
    "Boulder Rehandling": "alertTypes.boulderRehandling",
    "Dust Event": "alertTypes.dustEvent",
};

export const alertTypeLabel = (type: string): string => {
    const key = ALERT_TYPE_KEYS[type];
    return key ? i18n.t(key, { ns: "alerts" }) : type;
};

/**
 * Human-facing label for an alert `status`. The enum values ARE the wire values
 * — they are sent as `status_in` and matched against the API's own vocabulary —
 * so only this display path is translated. An unrecognised status renders
 * verbatim rather than blank.
 */
const ALERT_STATUS_KEYS: Record<AlertStatus, "status.pending" | "status.resolved" | "status.ignored"> = {
    [AlertStatus.Pending]: "status.pending",
    [AlertStatus.Resolved]: "status.resolved",
    [AlertStatus.Ignored]: "status.ignored",
};

export const alertStatusLabel = (status: string | null | undefined): string => {
    if (!status) return "—";
    const key = ALERT_STATUS_KEYS[status as AlertStatus];
    return key ? i18n.t(key, { ns: "alerts" }) : status;
};

/**
 * Operational severity carried on the alert (top-level `severity`, e.g. "medium").
 * Best-effort union — the backend may introduce more levels, so treat unknown
 * values as valid and render them verbatim rather than assuming this is closed.
 */
export type AlertSeverity = "low" | "medium" | "high" | "critical";

export interface AlertGPS {
    id: number;
    latitude: number;
    longitude: number;
    /** Elevation in metres; often null when there is no altitude fix. */
    altitude: number | null;
    timestamp: string;
    color: string | null;
    uuid: string;
}

export interface AlertImage {
    event_uuid: string;
    image_id: number;
    timestamp: string;
    classification: string;
    confidence: number;
    dump_image_valid: boolean;
    coverage_percentage: number | null;
    signed_url: string;
    /**
     * Multi-label classifications on the detail image (contract's
     * `ImageWithLabelsDTO.labels[]`). Additive: the flat `classification`/
     * `confidence` above stay for legacy callers; new detail UI should read the
     * primary active label from here (see {@link primaryImageLabel}).
     */
    labels?: AlertPreviewLabel[];
}

export interface Alert {
    id: number;
    timestamp: string;
    origin: string;
    type: string;
    event_uuid: string;
    status: AlertStatus;
    device_mxid: string;
    gps?: AlertGPS | null;
    /** Human-readable machine name — prefer over `device_mxid` in the UI. */
    machine_name?: string | null;
    location_name?: string | null;
    /**
     * IANA timezone of the alert's location (e.g. "Australia/Perth"). Shift and
     * "today" wall-clock must be computed in THIS zone, never the browser TZ.
     */
    location_timezone?: string | null;
    /** Number of frames in the event; drives the "N frames" badge. */
    image_count?: number | null;
    /** Event lifecycle stage, e.g. "dump". */
    event_state?: string | null;
    /** Free-form category, nullable on most alerts. */
    category?: string | null;
    /** Operational severity — see {@link AlertSeverity}. */
    severity?: AlertSeverity | null;

    /* -------------------------------------------------------------- *
     * Workflow projection — the alert's current position in the
     * review workflow. Drives the optimistic-concurrency loop
     * (send `review_cycle`/`activity_seq` as the expected state) and
     * the "closed by X" chip. All optional while the backend rolls
     * these out; treat absence as "unknown", not "cycle 0".
     * -------------------------------------------------------------- */
    /** Number of review cycles opened so far (0 = never reopened). */
    review_cycle?: number;
    /** Monotonic sequence of the latest activity — the concurrency token. */
    activity_seq?: number;
    /** When the alert was last closed (resolve/ignore); null while pending/open. */
    closed_at?: string | null;
    /** Human label of who closed it, for the "closed by X" chip; null while open. */
    closed_by_label?: string | null;
    /** Id of the activity that closed the alert; null while open. */
    closed_activity_id?: number | null;

    /* -------------------------------------------------------------- *
     * Pipeline latency (§5, backend-WIP) — per-alert delivery timing.
     * All nullable: absent for non-staff and legacy rows. The strip is
     * gated on `created_at != null` (no role check needed). `timestamp` is
     * frame capture, distinct from `predicted_at` (edge inference).
     * -------------------------------------------------------------- */
    /** Model inference time on the edge. */
    predicted_at?: string | null;
    /** Cloud ingest time. */
    created_at?: string | null;
    /** Live-notification dispatch time; null = not dispatched yet. */
    dispatched_at?: string | null;
    /** created_at − predicted_at (clamped ≥0) — upload + CF. */
    inference_latency_ms?: number | null;
    /** dispatched_at − created_at — our pipeline (skew-free). */
    dispatch_latency_ms?: number | null;
}

export interface AlertDetail extends Alert {
    preview: AlertImage | null;
    images: AlertImage[];
}

/* ------------------------------------------------------------------ *
 * New Alerts API contract — list / map / detail
 * GET /api/alerts/ , /api/alerts/map/ , /api/alerts/{id}/
 * ------------------------------------------------------------------ */

/**
 * Exactly one scope filter is allowed per request. Modelling it as a
 * discriminated union makes invalid combinations (two scopes at once)
 * unrepresentable — the serializer emits the single key that is present.
 */
export type AlertScope =
    | { device_mxid: string }
    | { device_mxids: string[] }
    | { machine_ids: number[] }
    | { location_id: number };

/** Optional server-side list/map filters. Confidence is a 0..1 fraction. */
export interface AlertListFilters {
    type_in?: string[];
    status_in?: AlertStatus[];
    label_in?: string[];
    min_confidence?: number;
    max_confidence?: number;
}

/** "Newest first" / "Oldest first". */
export type AlertOrdering = "-timestamp" | "timestamp";

/**
 * Input for GET /api/alerts/. The required tenant header
 * (`X-Minemaestro-Organization`) is injected globally by the axios
 * interceptor — it is NOT part of this input.
 */
export interface GetAlertsInput {
    scope: AlertScope;
    start_time: Date;
    end_time: Date;
    filters?: AlertListFilters;
    ordering?: AlertOrdering;
    include_preview?: boolean;
    limit?: number;
    offset?: number;
}

/**
 * A classification on the preview frame. The label + confidence used for
 * `label_in` / `min_confidence` filtering live HERE — not at the top level of
 * {@link AlertPreview}. Display the primary active row (see {@link primaryPreviewLabel}).
 */
export interface AlertPreviewLabel {
    classification: string;
    confidence: number | null;
    final_label: string | null;
    is_primary: boolean;
    is_active: boolean;
    review_state?: string | null;
    origin?: string | null;
}

/**
 * The preview frame embedded in list/map items when `include_preview=true`.
 * Distinct from the flat legacy {@link AlertImage} (AlertDetail.images): here the
 * classifications are an array under `labels`.
 */
export interface AlertPreview {
    image_id: number;
    event_uuid: string;
    device_mxid: string;
    timestamp: string;
    signed_url: string;
    dump_image_valid: boolean;
    event_frame?: number | null;
    total_frames?: number | null;
    machine_name?: string | null;
    labels: AlertPreviewLabel[];
}

/** Pick the label to show/filter on: the primary active one, else the first. */
const pickPrimaryLabel = (
    labels?: AlertPreviewLabel[] | null,
): AlertPreviewLabel | undefined =>
    labels?.find((l) => l.is_primary && l.is_active) ?? labels?.[0];

/** The classification to show/filter on: the primary active label, else the first. */
export const primaryPreviewLabel = (
    preview?: AlertPreview | null,
): AlertPreviewLabel | undefined => pickPrimaryLabel(preview?.labels);

/** Same selection as {@link primaryPreviewLabel}, for a detail {@link AlertImage}. */
export const primaryImageLabel = (
    image?: AlertImage | null,
): AlertPreviewLabel | undefined => pickPrimaryLabel(image?.labels);

/**
 * A list row. When `include_preview=true` the backend embeds a `preview` frame;
 * render it if present, otherwise fall back to a graceful empty state.
 */
export interface AlertListItem extends Alert {
    preview?: AlertPreview | null;
}

/**
 * A map point from GET /api/alerts/map/. Deliberately SLIM and FLAT — the
 * contract returns coordinates inline (`latitude`/`longitude`/`altitude`), NOT a
 * nested {@link AlertGPS}, and carries no `preview`. Just enough to plot a marker
 * and open the detail drawer by `id`.
 */
export interface AlertMapItem {
    id: number;
    timestamp: string;
    type: string;
    status: AlertStatus;
    device_mxid: string;
    event_uuid: string;
    latitude: number;
    longitude: number;
    /** Elevation in metres; null when there is no altitude fix. */
    altitude: number | null;
}

/**
 * Response wrapper for the map endpoint: a capped, unpaginated set. `truncated`
 * is true when `count` exceeded the server cap and points were dropped — surface
 * a "results capped" hint. `count` is the pre-cap total.
 */
export interface AlertMapResponse {
    count: number;
    truncated: boolean;
    items: AlertMapItem[];
}

/* ------------------------------------------------------------------ *
 * Activity timeline — GET /api/alerts/{id}/activities/
 * ------------------------------------------------------------------ */

/** Workflow event kind on the activity timeline. */
export type AlertActivityKind = "create" | "resolve" | "ignore" | "reopen" | "note";

/** Disposition attached to a resolve/ignore action. */
export type AlertResolutionDisposition =
    | "confirmed"
    | "false_positive"
    | "duplicate"
    | "not_actionable"
    | "insufficient_evidence"
    | "other";

/** Where the action originated. Open union — unknown surfaces render verbatim. */
export type AlertSurface = "platform" | "edge" | "api" | (string & {});

/** Kind of principal that performed the action. Open union. */
export type AlertPrincipalType = "user" | "service" | "system" | (string & {});

/** Authority scope of the principal — drives the "elevated" actor treatment. */
export type AlertAuthorityScope = "organization" | "platform" | "global" | (string & {});

/**
 * One entry on the alert's workflow timeline. The `principal_*` fields describe
 * WHO acted (identity + authority) and WHERE from (`surface`); render them with
 * the actor treatment (avatar shape = kind, weight = authority), not flat tags —
 * see `alerts/utils/activity-principal.ts`.
 */
export interface AlertActivityDTO {
    uuid: string;
    seq: number;
    review_cycle: number;
    kind: AlertActivityKind;
    status_before: AlertStatus | null;
    status_after: AlertStatus | null;
    resolution_disposition: AlertResolutionDisposition | null;
    reason_code: string | null;
    comment: string | null;
    metadata: Record<string, unknown>;
    surface: AlertSurface;
    principal_type: AlertPrincipalType;
    principal_role_code: string | null;
    principal_authority_scope: AlertAuthorityScope;
    /** Human-readable role+scope, e.g. "Organization operator". */
    principal_context_label: string | null;
    /** Person/actor name, e.g. "Jane Doe"; null for unnamed automated principals. */
    principal_label: string | null;
    target_label: string | null;
    created_at: string;
}

/*
 * Removed for the release-candidate contract: the `/resolve/`, `/ignore/`,
 * `/reopen/`, `/notes/`, `/live/state/`, `/kpis/`, and `/shifts/` endpoints and
 * their request/response DTOs no longer exist. Operator writes, live hints,
 * analytics, and monitoring now live in `modules/alerts/api/*` against the
 * single `POST /api/alerts/{id}/actions/`, `/live/subscribe/`, `/analytics/`,
 * and `/monitoring/alerts/` endpoints.
 */

/*
* API Response structure:
* {
*   "id": 974,
*   "timestamp": "2025-08-08T11:08:13Z",
*   "origin": "AI",
*   "type": "Boulder Handling",
*   "event_uuid": "522668c5-86af-4467-9f52-96fa2a898d9f",
*   "status": "Pending",
*   "device_mxid": "184430102141570F00",
*   "gps": {
*     "id": 10341006,
*     "latitude": -16.740582333333332,
*     "longitude": -41.904687,
*     "altitude": 307.3,
*     "timestamp": "2025-08-08T12:08:12Z",
*     "color": "#0000FF",
*     "uuid": "9c59511c-41ef-4709-b7f4-1fe94268f3cb"
*   }
* }
*/
